import { useEffect, useRef, type ReactNode } from "react";

/**
 * Sprint 24 Wave D — PR-D1: minimal off-canvas drawer for the
 * Admin Console mobile nav.
 *
 * Mirrors `apps/web/src/layout/MobileNavDrawer.tsx` (Sprint 16
 * Wave F + Sprint 17 Wave F focus-trap completion). Behavior:
 *   - Renders nothing when `open` is false (no offscreen DOM).
 *   - Esc closes the drawer.
 *   - Clicking the scrim closes the drawer.
 *   - Focus moves to the drawer panel on open so subsequent Tab
 *     keys cycle through the drawer's interactive children.
 *   - Restores focus to the previously-focused element on close.
 *   - Locks page scroll while open.
 *   - Tab from the last focusable wraps to the first.
 *   - Shift+Tab from the first focusable (or the panel itself)
 *     wraps to the last.
 *
 * Kept local to apps/admin so the admin shell does not depend on
 * apps/web. The two files can be merged into the design-system
 * package later when the breakpoint pattern is generalized.
 */
export function MobileNavDrawer({
  open,
  onClose,
  ariaLabel,
  children,
}: {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  children: ReactNode;
}): React.ReactNode {
  const panelRef = useRef<HTMLElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = (document.activeElement as HTMLElement) ?? null;
    panelRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function focusables(): HTMLElement[] {
      const root = panelRef.current;
      if (!root) return [];
      const nodes = root.querySelectorAll<HTMLElement>(
        [
          "a[href]",
          "button:not([disabled])",
          "input:not([disabled]):not([type='hidden'])",
          "select:not([disabled])",
          "textarea:not([disabled])",
          "[tabindex]:not([tabindex='-1'])",
        ].join(","),
      );
      return Array.from(nodes).filter((el) => !el.hasAttribute("inert"));
    }

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const list = focusables();
      if (list.length === 0) {
        e.preventDefault();
        panelRef.current?.focus();
        return;
      }
      const first = list[0]!;
      const last = list[list.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === panelRef.current) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = prevOverflow;
      previouslyFocusedRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          background: "color-mix(in oklab, black 40%, transparent)",
          zIndex: 50,
        }}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        style={{
          position: "fixed",
          insetBlockStart: 0,
          insetInlineStart: 0,
          blockSize: "100dvh",
          inlineSize: "min(80vw, 320px)",
          background: "var(--gt-sys-surface)",
          borderInlineEnd: "1px solid var(--gt-sys-border)",
          padding: "var(--gt-sys-space-6)",
          zIndex: 51,
          overflowY: "auto",
          outline: "none",
        }}
      >
        {children}
      </aside>
    </>
  );
}
